import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { ceil, chunk, groupBy, last, range, includes } from 'lodash'
import Checkbox from '../../../../../components/forms/Checkbox'
import i18n from 'simple-react-i18n'
import { MEASURE_COTE, PIEZO_FOLLOW_L_STORAGE } from '../../../../constants/PiezometryConstants'
import Line from '../../../../../components/echart/series/Line'
import { getColorFromPalette } from '../../../../../utils/ColorUtil'
import { PREVIOUSYEARS } from '../../constants/PiezometerSuiviConstants'
import DtoPiezoMeasureLight from '../../../../dto/chart/DtoPiezoMeasureLight'
import { Card, CardContent, Grid } from '@mui/material'
import { round } from '../../../../../utils/NumberUtil'
import { CardTitle } from '../../../../../components/card/NewCard'
import useLocalStorage from 'utils/customHook/useLocalStorage'

const PiezoSuiviPastYearsTab2 = ({
    displayCote,
    landmarkValue, // sers à caluler la profondeur : depth = landmarkValue - NGF
    changeParent, // met à jour les state du parent (dont les séries liées à cette tab)
    measures,
    time, // tab sélectionné en haut à gauche
    minDate,
    maxDate,
    tab,
    typeId,
    isPiezo,
}) => {
    const [activeYears, setActiveYears] = useLocalStorage(PIEZO_FOLLOW_L_STORAGE.ACTIVE_YEARS_PIEZOMETRIC_FOLLOW, [])
    const [groupedCivil, setGroupedCivil] = useState({})
    const [groupedHydro, setGroupedHydro] = useState({})

    const yearRange = useMemo(() => range(moment(measures[0]?.date).year(), moment(last(measures)?.date).year() + 1).filter(y => y !== moment().year()), [measures])

    useEffect(() => {
        setActiveYears(prev => prev.filter(y => includes(yearRange, y)))
    }, [yearRange])

    useEffect(() => {
        setGroupedCivil(groupBy(measures, m => moment(m.date).year()))
        setGroupedHydro(groupBy(measures, m => moment(m.date).add(3, 'month').year()))
    }, [measures, typeId])

    useEffect(() => {
        const toEchartMeasure = (m) => {
            return ({
                value: [
                    m.date,
                    isPiezo ? (displayCote === MEASURE_COTE.NGF ? m.NGF : round(landmarkValue - m.NGF)) : m.value,
                    { NGF: m.NGF, depth: round(landmarkValue - m.NGF) },
                ], isPiezo,
            })
        }
        const group = time === 'CIVIL_YEAR' || time === 'OTHER' ? groupedCivil : (time === 'HYDRO_YEAR' ? groupedHydro : null)
        if (!group) {
            changeParent({ yearSeries: [] })
        } else {
            const yearSeries = yearRange.flatMap((y, idx) => {
                if (!activeYears.includes(y)) {
                    return []
                }
                const yearMeasures = (() => {
                    if (time === 'CIVIL_YEAR' || time === 'OTHER') {
                        const nowYear = time === 'OTHER' && (minDate ?? maxDate) ? moment(minDate ?? maxDate).year() : moment().year()
                        return (group[y] || []).map(m => toEchartMeasure({ ...m, date: moment(m.date).year(nowYear).valueOf() }))
                    }
                    return (group[y] || []).map(m => toEchartMeasure({ ...m, date: moment(m.date).add(3, 'month').year(moment().year()).subtract(3, 'month').valueOf() }))
                })()
                if (!yearMeasures.length) {
                    return []
                }
                return [Line({
                    data: yearMeasures,
                    name: y.toString(),
                    isPiezo,
                    connectNulls: false,
                    showSymbol: false,
                    color: getColorFromPalette(idx),
                })]
            })
            changeParent({ yearSeries })
        }
    }, [yearRange, activeYears, time, minDate, maxDate, isPiezo, groupedCivil, groupedHydro]) // recalculé lorsqu'on change les checkbox, ou la tab en haut à gauche

    const changeYear = (y, v) => setActiveYears(v ? [...activeYears, y] : activeYears.filter(y2 => y2 !== y))

    if (!measures.length || tab !== PREVIOUSYEARS) {
        return null
    }
    const yearCheckboxes = yearRange.map((y, idx) => (
        <Grid
            key={`year${y}`}
            container
            alignItems='center'
            sx={{ cursor: 'pointer' }}
            onClick={() => changeYear(y, !activeYears.includes(y))}
        >
            <Checkbox checked={activeYears.includes(y)} />
            <span>
                <span className={'arrests-level-panel'} style={{ color: getColorFromPalette(idx), backgroundColor: getColorFromPalette(idx) }}>OO</span>
                {y}
            </span>
        </Grid>
    ))
    const groups = chunk(yearCheckboxes, ceil(yearCheckboxes.length / 4) || 1)
    return (
        <Card className='margin-top-1'>
            <CardTitle title={i18n.previousYears} />
            <CardContent>
                <Grid container spacing={2} className='padding-1'>
                    <Grid size={12}>
                        <Grid
                            key='allYears'
                            container
                            alignItems='center'
                            sx={{ cursor: 'pointer' }}
                            onClick={() => activeYears?.length === yearRange.length ? setActiveYears([]) : setActiveYears(yearRange) }
                        >
                            <Checkbox checked={activeYears?.length === yearRange.length} />
                            {i18n.selectAll}
                        </Grid>
                    </Grid>
                    <Grid size={3}>
                        {groups[0] || []}
                    </Grid>
                    <Grid size={3}>
                        {groups[1] || []}
                    </Grid>
                    <Grid size={3}>
                        {groups[2] || []}
                    </Grid>
                    <Grid size={3}>
                        {groups[3] || []}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

PiezoSuiviPastYearsTab2.propTypes = {
    tab: PropTypes.string,
    displayCote: PropTypes.number,
    id: PropTypes.number,
    minDate: PropTypes.number,
    maxDate: PropTypes.number,
    typeId: PropTypes.number,
    isPiezo: PropTypes.bool,
    landmarkValue: PropTypes.number,
    changeParent: PropTypes.func,
    measures: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezoMeasureLight)),
    time: PropTypes.oneOf([
        PropTypes.number,
        PropTypes.string,
    ]),
}

export default PiezoSuiviPastYearsTab2
